<template>
  <v-container fluid>
    <dialog-confirm ref="delete-confirm">
      <div class="text-subtitle-1">
        {{ $t('are_you_sure_you_want_to_delete_this') }}
      </div>
    </dialog-confirm>
    <dialog-confirm ref="restore-confirm">
      <div class="text-subtitle-1">
        {{ $t('are_you_sure') }}
      </div>
    </dialog-confirm>
    <dialog-confirm ref="resend-activation">
      <div class="text-subtitle-1">
        {{ $t('resend_verification_confirmation_text') }}
      </div>
    </dialog-confirm>
    <div class="d-flex justify-space-between align-center">
      <h2>{{ $tc('user', 2) }}</h2>
      <div>
        <v-btn
          v-if="user.can('create_user') && model.id != null && !model.deleted"
          color="primary"
          outlined
          @click="resendActivation(model)">
          <v-icon class="mr-1 ml-n2">mdi-email</v-icon>
          {{ $t('resend_verification_email') }}
        </v-btn>
        <v-btn
          v-if="user.can('impersonate_auth') && model.id != null && !model.deleted"
          color="primary"
          outlined
          class="ml-2"
          @click="impersonate(model)">
          <v-icon class="mr-1 ml-n2">mdi-incognito</v-icon>
          {{ $t('impersonate') }}
        </v-btn>
        <span v-if="user.can('delete_user')" class="ml-2">
          <v-btn v-if="!model.deleted" color="danger" outlined @click="deleteUser" :loading="deleting">
            <v-icon>mdi-delete</v-icon>
            {{ $t('delete') }}
          </v-btn>
          <v-btn v-else color="warning" outlined @click="restoreUser" :loading="restoring">
            <v-icon>mdi-arrow-u-left-top</v-icon>
            {{ $t('restore') }}
          </v-btn>
        </span>
      </div>
    </div>
    <div v-if="userCanUpdate()">
      <div v-if="modelLoaded">
        <user-form :model="model"/>
      </div>
    </div>
    <div v-else v-html="$t('404_error_message' )"/>
  </v-container>
</template>

<script>

import UserForm from '@/components/user/UserForm'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import DialogConfirm from '@/components/shared/DialogConfirm'
import AuthUtil from '@/util/AuthUtil'

export default {
  name: 'UserUpdate',
  components: {
    UserForm,
    DialogConfirm
  },
  mixins: [
    CurrentUserMixin,
    BrowserUtilitiesMixin
  ],
  data () {
    return {
      model: {},
      deleting: false,
      restoring: false
    }
  },

  methods: {
    userCanUpdate () {
      if (this.user.can('update_user', 'all')) {
        return true
      }
      if (this.user.can('update_user', 'own') && this.user.id === this.model.id) {
        return true
      }
      return false
    },
    fetchModel () {
      const expand = [
        'customers',
        'roles',
        'user_settings',
        'role_permissions',
        'resource_restriction_rule_set'
      ]
      this.$orm('User')
        .find(this.$route.params.id, {
          params: {
            expand: expand.join(',')
          }
        })
        .then((user) => {
          this.model = user
        })
        .catch(error => {
          this.handleError(error)
        })
    },

    /**
     * Dispatches userModule/resetCustomerContext (so that the app will re-load
     * it given the new user's avaialble customers) and redirects the user to
     * the impersonate end-point
     *
     * @param user
     * @return undefined
     */
    impersonate (user) {
      AuthUtil.impersonateUser(user.id)
    },
    /**
     * Resends the activation code to the user
     * @param user
     * @returns {Promise<void>}
     */
    async resendActivation (user) {
      if (await this.$refs['resend-activation'].confirm()) {
        try {
          await this.$httpAdapter.get('/user/activation-resend/', { params: { user_id: user.id } })
          await this.$store.dispatch(
            'globalMessagingModule/showSuccessFlashBanner',
            'Activation email has been resent.'
          )
        } catch (error) {
          this.handleError(error)
        }
      }
    },
    /**
     * Send an API request to delete the user.
     * @param item
     * @returns {Promise<void>}
     */
    async deleteUser () {
      if (await this.$refs['delete-confirm'].confirm()) {
        try {
          this.deleting = true
          await this.model.destroy()
          this.$router.push('/users', () => {
            this.$store.dispatch(
              'globalMessagingModule/showSuccessFlashBanner',
              `${this.$i18n.tc('user')} ${this.model.email} ${this.$t('generic_delete_success')}`
            )
          })
        } catch (error) {
          this.handleError(error)
        }
      }
    },
    /**
     * Send an API request to restore/undelete the user.
     * @param item
     * @returns {Promise<void>}
     */
    async restoreUser () {
      if (await this.$refs['restore-confirm'].confirm()) {
        try {
          this.restoring = true
          await this.$orm('User').update(this.model.id, { deleted: null })
          this.$router.push('/users', () => {
            this.$store.dispatch(
              'globalMessagingModule/showSuccessFlashBanner',
              `${this.$tc('user')} ${this.model.email} ${this.$t('generic_restore_success')}`
            )
          })
        } catch (error) {
          this.handleError(error)
        }
      }
    }
  },
  created () {
    this.fetchModel()
  },
  computed: {
    modelLoaded () {
      return this.model.id != null
    }
  },
  mounted () {
    this.updateBrowserTitle(this.$t('edit_user'))
  }
}
</script>
